import { useTranslation } from 'react-i18next';
import { UserMinus } from 'lucide-react';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import { initials } from 'components/rating/RatingForm';
import { apiErrorMessage } from 'utils/errorMessage';
import type { InterventionRequest } from 'api/interventionRequestsApi';
import {
  followUpStatusLabel,
  solicitationPhrase,
  statusDot,
  statusTone,
} from 'pages/AdminInterventionRequests/vocabulary';
import { technicianName } from './upcomingAssignment';

/**
 * Who was already solicited on this mission, what each one answered, and the **two** gestures the
 * panel offers on them — withdraw one pending request, or release the mission.
 *
 * The follow-up screen's own card says the same thing and offers nothing (D36); this one is the
 * writing side, so they are two components rather than one with the actions made optional.
 */

interface SolicitedTechniciansProps {
  /** Every live request of the mission, newest first. Cancelled ones are filtered by the caller. */
  requests: InterventionRequest[];
  /** The request that was accepted, if any — it alone makes a release possible (R-072). */
  acceptor: InterventionRequest | null;
  /** Still awaiting an answer: with none and no acceptor, there is nothing left to release. */
  pendingCount: number;
  /** « 3 sollicités · 1 accepté · 2 dépassées », built by the caller from the same list. */
  recap: string;
  cancelError: unknown;
  onCancel: (request: InterventionRequest) => void;
  onRelease: () => void;
}

export const SolicitedTechnicians = ({
  requests,
  acceptor,
  pendingCount,
  recap,
  cancelError,
  onCancel,
  onRelease,
}: SolicitedTechniciansProps) => {
  const { t } = useTranslation();

  if (requests.length === 0) return null;

  return (
    <section className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2.5">
        <span className="text-[11px] font-semibold uppercase tracking-[.05em] text-muted-foreground/70">
          {t('Solicited technicians')}
        </span>
        <span className="text-[11.5px] text-muted-foreground/70">{recap}</span>
      </div>
      <ul className="flex flex-col gap-[7px]">
        {/* Chronological, oldest first: grouping by status would hide the order in which the
            mission actually went round, which is what the reader came for. */}
        {[...requests].reverse().map((request) => (
          <li
            key={request.id}
            className={cn('rounded-xl border p-3', request.status === 'accepted' && 'border-ring')}
          >
            {/* The name and the pill share the top line, the sentence owns the one below and the
                button the one after: reading the first and last name is what the card is for, so
                nothing else competes with it for width. */}
            <div className="flex items-start gap-[11px]">
              <span
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-zinc-100 text-[12px] font-semibold text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300"
                aria-hidden="true"
              >
                {initials(technicianName(request))}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <p className="min-w-0 break-words text-[13.5px] font-semibold">
                    {technicianName(request)}
                  </p>
                  <span
                    className={cn(
                      'inline-flex h-[23px] shrink-0 items-center gap-1.5 rounded-full pl-2 pr-[9px] text-xs font-medium',
                      statusTone(request.status)
                    )}
                  >
                    <span
                      className={cn('h-1.5 w-1.5 rounded-full', statusDot(request.status))}
                      aria-hidden="true"
                    />
                    {followUpStatusLabel(t, request.status)}
                  </span>
                </div>
                <p className="mt-0.5 text-[12.5px] text-muted-foreground">
                  {solicitationPhrase(t, request, acceptor ? technicianName(acceptor) : null)}
                </p>
              </div>
            </div>
            {/* On a pending request only: an answered one has nothing left to withdraw. The card
                is titled « Techniciens sollicités », so the button says only what it undoes. */}
            {request.status === 'pending' && (
              <div className="mt-2 flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  className="min-h-[44px] text-[12.5px]"
                  title={t('The request is withdrawn: the technician no longer sees it and is no longer reminded')}
                  onClick={() => onCancel(request)}
                >
                  {t('Cancel')}
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {/* R-072 — with nobody retained there is nothing to release, and the sentence says what to
          do instead rather than offering a button that would cancel nothing. */}
      {acceptor ? (
        <Button
          type="button"
          variant="outline"
          className="mt-1 min-h-[44px] gap-1.5 self-end text-[12.5px]"
          onClick={onRelease}
        >
          <UserMinus className="h-4 w-4" aria-hidden="true" />
          {t('Release the mission')}
        </Button>
      ) : (
        pendingCount === 0 && (
          <p className="text-[12px] text-muted-foreground">
            {t('No acceptor: nothing to release. Solicit other technicians below.')}
          </p>
        )
      )}
      {cancelError ? (
        <p className="text-[12.5px] text-rose-600 dark:text-rose-400" role="alert">
          {apiErrorMessage(t, cancelError)}
        </p>
      ) : null}
    </section>
  );
};
